import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { User, Mail, Phone } from "lucide-react";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

// Core contact fields: name, email, phone, title
export const coreContactFields = defineFieldConfigMap({
  // FIELD: name
  name: {
    type: "text",
    label: "Name",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 3,
    filterType: "contains",
    width: 250,
    minWidth: 200,
    maxWidth: 400,
    required: true,
    validation: {
      required: "Contact name is required.",
    },
    placeholder: "Enter contact name",
    icon: "User",
    // Role-based access control - visible to all roles
    allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
    // Show row actions inline with this column
    showRowActions: true,
    showOnCard: true,
    cardSpan: 2,
    mobileDetailSectionId: "basic",
    graphqlSelector: "name",
    savedViewMembership: [
      "all-contacts",
      "active-contacts",
      "customers",
      "leads",
      "prospects",
      "no-tickets-this-month",
      "with-tickets-this-month",
      "no-tickets-last-month",
      "with-tickets-last-month",
      "needs-follow-up",
      "contacts-with-contract",
      "contacts-without-contract",
      "cold-90-days",
      "contacts-with-active-deals",
      "contacts-without-active-deals",
    ],
  },
  // FIELD: email
  email: {
    type: "text",
    label: "Email",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 3,
    filterType: "contains",
    width: 220,
    minWidth: 180,
    maxWidth: 300,
    required: true,
    validation: {
      required: "Email is required.",
      pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    },
    placeholder: "Enter email address",
    icon: "Mail",
    // Role-based access control - visible to all roles
    allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
    graphqlSelector: "email",
    savedViewMembership: [
      "all-contacts",
      "active-contacts",
      "customers",
      "leads",
      "prospects",
      "no-tickets-this-month",
      "with-tickets-this-month",
      "no-tickets-last-month",
      "with-tickets-last-month",
      "needs-follow-up",
      "contacts-with-contract",
      "contacts-without-contract",
      "cold-90-days",
      "contacts-with-active-deals",
      "contacts-without-active-deals",
    ],
  },
  // FIELD: phone
  phone: {
    type: "text",
    label: "Phone",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    filterType: "contains",
    width: 150,
    minWidth: 120,
    maxWidth: 200,
    placeholder: "Enter phone number",
    icon: "Phone",
    // Role-based access control - visible to all roles
    allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
    graphqlSelector: "phone",
    savedViewMembership: [
      "all-contacts",
      "active-contacts",
      "customers",
      "leads",
      "prospects",
      "no-tickets-this-month",
      "with-tickets-this-month",
      "no-tickets-last-month",
      "with-tickets-last-month",
      "needs-follow-up",
      "contacts-with-contract",
      "contacts-without-contract",
      "cold-90-days",
      "contacts-with-active-deals",
      "contacts-without-active-deals",
    ],
  },
  // FIELD: title
  title: {
    type: "text",
    label: "Title",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    filterType: "contains",
    width: 150,
    minWidth: 120,
    maxWidth: 200,
    placeholder: "Enter job title",
    icon: "User",
    // Role-based access control - visible to all roles
    allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
    graphqlSelector: "title",
    // No saved view currently lists `title` as a visible column.
  },
});
